import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

const SLIDER_DIRECTION_EXPLANATION = String.raw`
The direction score checks whether each instrument's benchmark points in the
same direction as the user's slider.

### 1. Add the news impacts

Each news item has an **ICS impact value** for each instrument. Within one
period, all of an instrument's signed ICS values are added together to produce
that instrument's **PCS — Period Conviction Score**:

$$\mathrm{PCS}_{instrument}=\sum \mathrm{ICS\ impacts\ from\ news}$$

### 2. Calibrate PCS to the slider

PCS is calibrated onto the same $-5$ to $+5$ scale as the user's slider. This
keeps a very large raw PCS inside the slider range while preserving its
positive or negative direction.

### 3. Compare directions

PM uses the last slider value saved for that instrument in the period. If the
slider was never moved, its value is $0$.

- **Same direction:** full direction credit.
- **User leaves the slider at zero:** $45\%$ credit.
- **Opposite direction:** no direction credit.
- A calibrated PCS below $0.35$ in absolute value is treated as neutral and is
  not included in the direction comparison.

Instruments with stronger PCS values have more weight. The combined direction
score is worth up to **25 points per period**. Slider size is assessed
separately in the Slider magnitude component.
`;

const PillarOneSliderDirectionGuide: React.FC = () => (
  <aside
    aria-labelledby="pillar-one-slider-direction-title"
    className="assessment-inspect-assessment__pillar-one-guide"
  >
    <header className="assessment-inspect-assessment__pillar-one-guide-header">
      <div className="assessment-inspect-assessment__pillar-one-guide-kicker">
        <span>
          <InfoCircleOutlined aria-hidden="true" /> Scoring guide
        </span>
        <Tag color="blue">25% of Pillar 1</Tag>
      </div>
      <Typography.Title id="pillar-one-slider-direction-title" level={4}>
        Slider direction score
      </Typography.Title>
      <Typography.Text type="secondary">
        How each period’s slider signs become up to 25 points.
      </Typography.Text>
    </header>

    <div className="assessment-inspect-assessment__pillar-one-guide-body">
      <ReactMarkdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkMath]}>
        {SLIDER_DIRECTION_EXPLANATION}
      </ReactMarkdown>
    </div>
  </aside>
);

export default PillarOneSliderDirectionGuide;
